// Boot, in the order the four steps have to happen in.
//
// 1. `/config.json`, because nothing else can be configured without it.
// 2. MSAL, which needs the tenant and client id that document carries.
// 3. A sign-in, if nobody is signed in. It is a redirect, so this module runs
//    again afterwards and the account is there the second time.
// 4. The application.
//
// A failure at step 1 or 2 renders as text rather than a blank page. The
// ordinary cause is an app setting the stack did not write, and a white screen
// is the least diagnosable presentation of that.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { loadConfig } from './config';
import { account, initAuth, signIn } from './auth';
import './styles.css';

const container = document.getElementById('root');
if (!container) throw new Error('index.html has no #root');
const root = createRoot(container);

function fail(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  root.render(
    <main className="boot-failure">
      <h1>Barrier Tracker cannot start</h1>
      <pre>{message}</pre>
    </main>
  );
}

async function boot(): Promise<void> {
  await loadConfig();
  await initAuth();

  if (!account()) {
    root.render(
      <main className="boot-signin">
        <h1>Barrier Tracker</h1>
        <p>Sign in with your work account.</p>
        <button className="primary" onClick={() => void signIn()}>
          Sign in
        </button>
      </main>
    );
    return;
  }

  root.render(
    <StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StrictMode>
  );
}

boot().catch(fail);
